import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { JdcManagerService } from '../jdc-manager.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

@Component({
  selector: 'gtw-jdc-save-task-modal',
  templateUrl: './jdc-save-task-modal.component.html',
  styleUrls: ['./jdc-save-task-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class JdcSaveTaskModalComponent implements OnChanges, OnDestroy {
  @Input() mode: 'new' | 'edit' = 'new';
  @Input() rowData: any = null;
  @Input() channelList: any[] = [];
  @Input() taskList: any[] = [];
  @Input() jcdKey: any;
  @Input() clientKey: any;
  @Input() selectedChannel: any = null;
  @Input() client: any = null;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  taskModalMinimise = false;
  taskLoading = false;
  crudLoading = false;

  taskForm: any = {
    task_key: null,
    task_name: '',
    task_desc: '',
    task_priority: 1,
    task_status: 'A',
    channel: null,
    parentTask: '',
  };

  statusList = [
    { name: 'Active', value: 'A' },
    { name: 'Inactive', value: 'I' },
  ];

  modalTaskList: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private jdcManagerService: JdcManagerService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  get title(): string {
    return this.mode === 'edit' ? 'Edit Task' : 'New Task';
  }

  ngOnChanges(): void {
    this.taskModalMinimise = false;
    this.initForm();
    this.loadTasksForChannel();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closed.emit();
  }

  clearForm(): void {
    const channel = this.taskForm.channel;
    this.taskForm = {
      task_key: this.mode === 'edit' ? this.taskForm.task_key : null,
      task_name: '',
      task_desc: '',
      task_priority: 1,
      task_status: 'A',
      channel,
      parentTask: '',
    };
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) return;
    if (!this.taskForm.channel || !this.taskForm.task_name?.trim() ||
        !this.taskForm.task_desc?.trim() || this.taskForm.task_priority == null ||
        !this.taskForm.task_status) {
      this.gtwSnackbarService.openSnackBar('Required fields are missing.', 'gtw-snackbar--red');
      return;
    }
    const name = this.taskForm.task_name.trim();
    if (this.mode === 'new' && this.modalTaskList.some((t) => t.task_name === name)) {
      this.gtwSnackbarService.openSnackBar('Task already exists.', 'gtw-snackbar--red');
      return;
    }
    this.crudLoading = true;
    const payload = [{
      TASK_PRIORITY: Number(this.taskForm.task_priority) || 1,
      TASK_KEY: this.mode === 'edit' ? this.taskForm.task_key : undefined,
      TASK_NAME: name,
      TASK_DESC: this.taskForm.task_desc,
      TASK_STATUS: this.taskForm.task_status,
      JCD_KEY: this.jcdKey,
      PARENT_TASK_KEY: this.taskForm.parentTask || '',
      CLIENT_KEY: this.clientKey,
    }];
    const actionCode = this.mode === 'edit' ? '4dk04a' : '6aoylm';
    this.jdcManagerService.saveJsonAction(actionCode, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.crudLoading = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              `Task ${this.mode === 'edit' ? 'saved' : 'created'} successfully.`,
              'gtw-snackbar--green'
            );
            this.saved.emit();
          } else {
            this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Problem saving task.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar('Problem saving task.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onChannelChange(): void {
    this.loadTasksForChannel();
  }

  private initForm(): void {
    const channel = this.resolveChannel(this.rowData?.channel ?? this.selectedChannel);
    if (this.mode === 'edit' && this.rowData) {
      this.taskForm = {
        task_key: this.rowData.task_key,
        task_name: this.rowData.task_name || '',
        task_desc: this.rowData.task_desc || '',
        task_priority: this.rowData.task_priority ?? 1,
        task_status: this.rowData.task_status || 'A',
        channel,
        parentTask: this.rowData.parent_task_key || this.rowData.parentTask || '',
      };
    } else {
      this.taskForm = {
        task_key: null,
        task_name: '',
        task_desc: '',
        task_priority: 1,
        task_status: 'A',
        channel,
        parentTask: '',
      };
    }
    this.modalTaskList = this.taskList || [];
    this.cdr.detectChanges();
  }

  private resolveChannel(channel: any): any {
    if (!channel) return this.channelList[0] ?? null;
    const key = channel.channel_key;
    const name = channel.channel_name;
    return this.channelList.find((c) =>
      (key != null && c.channel_key === key) || (name && c.channel_name === name)
    ) ?? channel;
  }

  private loadTasksForChannel(): void {
    const ch = this.taskForm.channel;
    const ck = this.client?.client_key ?? this.clientKey;
    if (!ch?.channel_key || ck == null) {
      this.modalTaskList = [];
      this.cdr.detectChanges();
      return;
    }
    const filterParams = {
      client: { value: ck },
      jurisdiction: { value: 1 },
      domain: { value: 9 },
      channel: { value: ch.channel_key },
    };
    this.taskLoading = true;
    this.jdcManagerService.getTaskForJCD(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const list = res?.taskList ?? res?.data?.taskList ?? (Array.isArray(res) ? res : []);
          this.modalTaskList = this.flattenTaskList(list);
          this.taskLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.modalTaskList = this.taskList || [];
          this.taskLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private flattenTaskList(tree: any[]): any[] {
    const flat: any[] = [];
    const recurse = (nodes: any[], level: number) => {
      (nodes || []).forEach((n: any) => {
        flat.push({
          task_name: n.task_name,
          task_key: n.task_key,
          indent: '\u00A0\u00A0\u00A0'.repeat(level),
        });
        recurse(n.sub_task || [], level + 1);
      });
    };
    recurse(tree || [], 0);
    return flat;
  }
}
